import {IonInput} from '@ionic/angular';
import {Subscription} from 'rxjs';
import AutocompleteService = google.maps.places.AutocompleteService;
import LatLngBounds = google.maps.LatLngBounds;
import Map = google.maps.Map;
import Marker = google.maps.Marker;
import LatLng = google.maps.LatLng;
import Geocoder = google.maps.Geocoder;
import GeocoderResult = google.maps.GeocoderResult;
import GeocoderStatus = google.maps.GeocoderStatus;
import AutocompleteSessionToken = google.maps.places.AutocompleteSessionToken;
import ComponentRestrictions = google.maps.places.ComponentRestrictions;
import PlacesServiceStatus = google.maps.places.PlacesServiceStatus;
import AutocompletePrediction = google.maps.places.AutocompletePrediction;

import MarkerClusterer from '@googlemaps/markerclustererplus';

import Address from 'src/smoothr-web-app-core/models/Address';
import {getLatLng, sleep} from 'src/smoothr-web-app-core/utils/utils';
import {Api} from 'src/smoothr-web-app-core/api/api';
import {ValidationUtils} from 'src/smoothr-web-app-core/utils/validation-utils';
import {NominatimPlace} from 'src/smoothr-web-app-core/models/NominatimPlace';
import Venue from 'src/smoothr-web-app-core/models/Venue';
import Order from 'src/smoothr-web-app-core/models/Order';
import {TranslateService} from '@ngx-translate/core';

export const selectedMark = '/assets/eam/maps_marker_selected.svg';
export const notSelectedMark = '/assets/eam/maps_marker.svg';
export const userMark = '/assets/icon/userMark.svg';
// google.maps.Marker.getPosition() can return null/undefined (e.g. when the Maps JS API
// failed to authorize). Comparing coordinates must never throw, otherwise the whole map
// setup aborts and leaves the venue list stuck in its loading state.
const markerIsAt = (mark: Marker, lat: number, lng: number): boolean => {
	const position = mark?.getPosition();
	if (!position) {
		return false;
	}
	return position.lat() === lat && position.lng() === lng;
};
const markerMatchesVenue = (mark: Marker, ven: Venue): boolean => {
	const coordinates = ven?.location?.coordinates;
	if (!coordinates) {
		return false;
	}
	return markerIsAt(mark, coordinates[1], coordinates[0]);
};
const sessionToken = new AutocompleteSessionToken();
const componentRestrictions: ComponentRestrictions = {
	country: ['de', 'au']
};
const componentForm = {
	street_number: 'short_name',
	route: 'long_name',
	locality: 'long_name',
	administrative_area_level_1: 'long_name',
	country: 'short_name',
	postal_code: 'short_name'
};
export class MapsUtils {
	static initAutocomplete(
		ionInput: IonInput,
		callback: (predictions: Address[]) => void,
		loadingCallback: (loading: boolean) => void
	): Subscription {
		let prevRequest: Promise<Address[]> = null;
		return ionInput.ionChange.subscribe(async event => {
			if (prevRequest) {
				await prevRequest;
				await sleep(100);
			}
			loadingCallback(true);
			const value = event.detail.value;
			if (value.replace(' ', '').length === 0) {
				callback([]);
				loadingCallback(false);
				return;
			}
			prevRequest = new Promise<Address[]>(async (resolve, reject) => {
				try {
					resolve(await this.getNominatimPrediction(value));
				} catch (e) {
					reject(e);
				}
			});
			callback(await prevRequest);
			loadingCallback(false);
			prevRequest = null;
		});
	}
	static async initAutocompleteGoogle(
		ionInput: IonInput,
		callback: (predictions: AutocompletePrediction[]) => void
	): Promise<Subscription> {
		const service = new AutocompleteService();
		return ionInput.ionChange.subscribe(event => {
			const value = event.detail.value;
			if (value.replace(' ', '').length === 0) {
				callback([]);
				return;
			}
			service.getPlacePredictions(
				{
					input: value,
					sessionToken,
					componentRestrictions
				},
				async (predictions, status) => {
					if (status !== PlacesServiceStatus.OK) {
						callback([]);
						return;
					}
					predictions = predictions.filter(it =>
						it.types.find(type => type === 'street_address' || type === 'geocode')
					);
					callback(predictions);
				}
			);
		});
	}
	static async fillInPlaceGoogle(
		translate: TranslateService,
		ionInput: IonInput,
		place: GeocoderResult,
		searchTerm: (searchTerm: string | null) => string
	): Promise<Address> {
		if (!place || !place.address_components) {
			return null;
		}
		const address = MapsUtils.placeToAddressGoogle(place);
		const addressError = this.validateAddressGoogle(translate, address, false);
		if (addressError) {
			console.error('Maps error: ' + addressError);
			throw addressError;
		}
		if (place.formatted_address) {
			ionInput.getInputElement().then(el => {
				el.value = place.formatted_address;
				searchTerm(el.value);
			});
		}
		address.lat = place.geometry.location.lat();
		address.lng = place.geometry.location.lng();
		return address;
	}
	static validateAddressGoogle(translate: TranslateService, address: Address, strong: boolean = false): string {
		if (strong && !address.street) {
			return translate.instant('address.street');
		}
		if (!address.city) {
			return translate.instant('address.city');
		}
		if (!address.state) {
			return translate.instant('address.state');
		}
		if (!address.country) {
			return translate.instant('address.country');
		}
		if (strong && !address.postalCode) {
			return translate.instant('address.postalCode');
		}
		return null;
	}
	static async getNominatimPrediction(input: string): Promise<Address[]> {
		const data = (await Api.mapSearch(input, 3)).data;
		const addresses = data
			.filter(
				nplace =>
					nplace.category === 'place' ||
					nplace.category === 'building' ||
					nplace.category === 'highway' ||
					nplace.category === 'man_made' ||
					nplace.category === 'boundary' ||
					nplace.category === 'shop' ||
					nplace.category === 'amenity'
			)
			.map(nplace => this.placeToAddress(nplace))
			.filter(address => !ValidationUtils.validateAddress(address, true, true));
		const titles = addresses.map(address => this.addressToString(address));
		return addresses.filter((address, addressIndex) => {
			return titles.indexOf(titles[addressIndex]) === addressIndex;
		});
	}

	// 	static async getGooglePrediction(input: string): Promise<GoogleAutoCompletePrediction[]> {
	// 		return (await Api.googleAutoComplete(input)).data.predictions as GoogleAutoCompletePrediction[];
	// 	}

	static checkAddress(address: Address): string {
		const addressError = ValidationUtils.validateAddress(address, false, false);
		if (addressError) {
			console.error('Maps error: ' + addressError);
			throw addressError;
		}
		return this.addressToString(address);
	}

	static async executeSearch(ionInput: IonInput): Promise<Address> {
		return new Promise<Address>(async (resolve, reject) => {
			const input = await ionInput.getInputElement();
			const inputValue = input.value;
			if (!inputValue || inputValue.length <= 0) {
				reject();
				return;
			}
			try {
				const result = MapsUtils.getPlace(inputValue);
				if (result) {
					resolve(result);
				} else {
					reject('No results');
				}
				return;
			} catch (e) {
				reject(e);
			}
		});
	}

	static async getPlace(pred: string): Promise<Address> {
		return new Promise<Address>(async (resolve, reject) => {
			try {
				// 				try {
				const predictions = await this.getNominatimPrediction(pred);
				if (predictions.length < 1) {
					resolve(null);
					return;
				}
				resolve(predictions[0]);
				// 				} catch (e) {
				// 					const predictions = await this.getGooglePrediction(pred);
				// 					if (predictions.length < 1) {
				// 						resolve(null);
				// 					}
				// 					const place = await this.getGooglePlace(predictions[0].place_id);
				// 					resolve(place);
				// 				}
			} catch (e) {
				reject(e);
			}
		});
	}

	// 	static async getGooglePlace(placeId: string): Promise<Address> {
	// 		const googlePlaceDetails = (await Api.googlePlaceDetails(placeId)).data.result;
	// 		return this.placeToAddress(googlePlaceDetails) as Address;
	// 	}

	static placeToAddress(place: NominatimPlace): Address {
		if (!place) {
			return null;
		}
		// 		if ((place as any).address_components) {
		// 			return this.googlePlaceToAddress(place as GooglePlaceDetailsResult);
		// 		}
		// 		place = place as NominatimPlace;
		const address = new Address();
		address.number = place.address?.house_number || place.address?.house_name || null;
		address.street = place.address?.road || place.address?.construction || null;
		address.city =
			place.address?.city || place.address?.town || place.address?.village || place.address?.municipality || null;
		address.state =
			place.address?.state ||
			place.address?.region ||
			place.address?.state_district ||
			place.address?.county ||
			place.address?.city ||
			null;
		address.country = place.address?.country || place.address?.country_code || null;
		address.postalCode = place.address?.postcode || null;
		address.lat = +place.lat || null;
		address.lng = +place.lon || null;
		address.placeId = place?.place_id;
		address.displayName = place?.display_name;
		return address;
	}

	static placeToAddressGoogle(place: GeocoderResult): Address {
		const address = new Address();
		for (const component of place.address_components) {
			const addressType = component.types[0];
			if (componentForm[addressType]) {
				const val = component[componentForm[addressType]];
				switch (addressType) {
					case 'street_number':
						address.number = val;
						break;
					case 'route':
						address.street = val;
						break;
					case 'locality':
						address.city = val;
						break;
					case 'administrative_area_level_1':
						address.state = val;
						break;
					case 'country':
						address.country = val;
						break;
					case 'postal_code':
						address.postalCode = val;
						break;
					default:
						break;
				}
			}
		}
		address.lat = place.geometry.location.lat();
		address.lng = place.geometry.location.lng();
		return address;
	}

	static addressToString(address: Address): string {
		if (!address) {
			return '';
		}
		if (address.displayName?.length > 0) {
			return address.displayName;
		}
		let title = '';
		if (address.street) {
			title += address.street;
		}
		if (address.number) {
			title += ' ' + address.number;
		}
		if (title !== '' && (address.postalCode || address.city)) {
			title += ',';
		}
		if (address.postalCode) {
			if (title !== '') {
				title += ' ';
			}
			title += address.postalCode;
		}
		if (address.city) {
			if (title !== '') {
				title += ' ';
			}
			title += address.city;
		}

		return title;
	}

	static async getUserGeocode(): Promise<Address> {
		var options = {
			enableHighAccuracy: true,
			timeout: 5000,
			maximumAge: 0
		};
		const geocoderPromise = new Promise<Address>((resolve, reject) => {
			navigator.geolocation.getCurrentPosition(
				async position => {
					try {
						console.log(position);
						const result = (await Api.geocode(position.coords.latitude, position.coords.longitude)).data;
						if (result) {
							resolve(MapsUtils.placeToAddress(result));
						} else {
							reject('No result');
						}
					} catch (e) {
						reject(e);
					}
				},
				error => reject(error),
				(options = options)
			);
		});
		const timeoutPromise = new Promise<Address>((resolve, reject) => {
			setTimeout(() => reject('Timeout'), 10000);
		});
		return Promise.race([geocoderPromise, timeoutPromise]);
	}

	static addVenuesToMap(
		clusterer: MarkerClusterer,
		selectedVenue: Venue,
		venues: Venue[],
		map: Map,
		isShowUserCoordinates: boolean = false,
		userCoordinates: {lan: number; lat: number} = {lan: 0, lat: 0},
		onClick: (venue: Venue) => void
	): MarkerClusterer {
		clusterer =
			clusterer ||
			new MarkerClusterer(map, [], {
				gridSize: 30
			});
		const newVenues = venues.filter(
			ven => clusterer.getMarkers().find(mark => markerMatchesVenue(mark, ven)) === undefined
		);
		const marksToRemove = clusterer
			.getMarkers()
			.map((mark, index) => (venues.find(ven => markerMatchesVenue(mark, ven)) === undefined ? index : -1))
			.filter(i => i >= 0);
		for (const markToRemove of marksToRemove.reverse()) {
			clusterer.removeMarker(clusterer.getMarkers()[markToRemove]);
		}
		let venueIndex = 0;
		const bounds = new LatLngBounds();
		clusterer.getMarkers().forEach(mark => mark.setIcon(notSelectedMark));
		for (const venue of venues) {
			const latLng = getLatLng(venue);
			if (!latLng) {
				continue;
			}
			const position = new LatLng(latLng.latitude, latLng.longitude);
			if (latLng.latitude !== 0 && latLng.longitude !== 0) {
				bounds.extend(position);
			}
			if (newVenues.find(ven => ven._id === venue._id)) {
				const marker = new Marker({
					position,
					icon: notSelectedMark,
					animation: google.maps.Animation.DROP,
					clickable: true
				});
				marker.addListener('click', () => {
					onClick(venue);
				});
				clusterer.addMarker(marker, true);
				venueIndex++;
			}
		}
		if (!isShowUserCoordinates) {
			const markToSelect = clusterer
				.getMarkers()
				.findIndex(mark => selectedVenue && markerMatchesVenue(mark, selectedVenue));
			const posToSelect = markToSelect >= 0 ? clusterer.getMarkers()[markToSelect].getPosition() : null;
			if (posToSelect) {
				clusterer.getMarkers()[markToSelect].setIcon(selectedMark);
				const pos = posToSelect;
				const selectedBounds = new LatLngBounds();
				selectedBounds.extend(new LatLng(pos.lat() - 0.01, pos.lng() - 0.01));
				selectedBounds.extend(new LatLng(pos.lat() + 0.01, pos.lng() + 0.01));
				map.fitBounds(selectedBounds);
				map.panTo(pos);
			} else if (!bounds.isEmpty()) {
				map.fitBounds(bounds);
				map.panToBounds(bounds);
			} else {
				map.setZoom(6);
				map.panTo(new LatLng(51.0968582, 5.9690268));
			}
		} else {
			const position = new LatLng(userCoordinates.lat, userCoordinates.lan);
			console.log(position);
			bounds.extend(position);
			const marker = new Marker({
				position,
				icon: notSelectedMark,
				animation: google.maps.Animation.DROP,
				clickable: true
			});

			clusterer.addMarker(marker, true);
			const markToSelect = clusterer
				.getMarkers()
				.findIndex(mark => markerIsAt(mark, userCoordinates.lat, userCoordinates.lan));
			const pos = markToSelect >= 0 ? clusterer.getMarkers()[markToSelect].getPosition() : null;
			if (!pos) {
				return clusterer;
			}
			clusterer.getMarkers()[markToSelect].setIcon(userMark);
			const selectedBounds = new LatLngBounds();
			selectedBounds.extend(new LatLng(pos.lat() - 0.01, pos.lng() - 0.01));
			selectedBounds.extend(new LatLng(pos.lat() + 0.01, pos.lng() + 0.01));
			map.fitBounds(selectedBounds);
			map.panTo(pos);
		}

		return clusterer;
	}
	static async executeSearchGoogleSearch(ionInput: IonInput): Promise<GeocoderResult> {
		return new Promise<GeocoderResult>(async (resolve, reject) => {
			const input = await ionInput.getInputElement();
			const inputValue = input.value;
			if (!inputValue || inputValue.length <= 0) {
				reject();
				return;
			}
			const service = new AutocompleteService();
			service.getPlacePredictions(
				{
					input: input.value,
					sessionToken,
					componentRestrictions
				},
				async (predictions, status) => {
					if (status !== PlacesServiceStatus.OK) {
						reject();
						return;
					}
					if (predictions.length > 0) {
						try {
							resolve(await MapsUtils.getPlaceGoogle(predictions[0].description));
						} catch (e) {
							reject(e);
						}
					} else {
						resolve(null);
					}
				}
			);
		});
	}
	static async getPlaceGoogle(pred: string): Promise<GeocoderResult> {
		return new Promise<GeocoderResult>((resolve, reject) => {
			const service = new Geocoder();
			service.geocode(
				{
					address: pred
				},
				(result, status) => {
					if (status === GeocoderStatus.OK && result.length > 0) {
						resolve(result[0]);
						return;
					} else {
						reject(status);
						return;
					}
				}
			);
		});
	}
}

export const calculateGeoDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
	const r = 6371; // Radius of the earth in km
	const dLat = deg2rad(lat2 - lat1); // deg2rad below
	const dLon = deg2rad(lon2 - lon1);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return r * c; // Distance in km
};

export const deg2rad = (deg: number) => {
	return deg * (Math.PI / 180);
};

export const venueToAddress = (venue: Venue) => {
	const address = new Address();
	address.city = venue.city.de;
	address.lat = venue.location.coordinates[1];
	address.lng = venue.location.coordinates[0];
	address.street = venue.street;
	address.number = venue.number;
	address.postalCode = venue.postalCode;
	return address;
};

export const orderToAddress = (order: Order) => {
	const address = new Address();
	address.city = order.preorder.city;
	address.lat = order.preorder.lat;
	address.lng = order.preorder.lng;
	address.street = order.preorder.street;
	address.number = order.preorder.number;
	address.postalCode = order.preorder.postalCode;
	return address;
};
